import React, { useState, useEffect } from 'react';
import { FaEdit, FaSave, FaUndo, FaPlus, FaCode, FaEnvelope, FaFilePdf, FaInfoCircle } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import { logoAbsoluteUrl } from '../../constants/branding';

const TemplateManager = () => {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editContent, setEditContent] = useState('');
    const [editSubject, setEditSubject] = useState('');
    const [viewMode, setViewMode] = useState('code'); // 'code' or 'preview'
    const [message, setMessage] = useState({ type: '', text: '' });

    const mockData = {
        customerName: 'John Citizen',
        invoiceNumber: 'INV-2026-001',
        totalAmount: '$120.00',
        amountPaid: '$120.00',
        amountDue: '$120.00',
        dueDate: '15 February 2026',
        invoiceDate: '01 February 2026',
        planName: 'NBN100 FTTP Limitless',
        portalUrl: '#',
        downloadUrl: '#',
        portalInvoiceUrl: '#',
        portalPaymentUrl: '#',
        errorMessage: 'The payment was declined by the bank.',
        lineItemsHtml: '<tr><td>NBN Service</td><td class="text-right">1</td><td class="text-right">$109.09</td><td class="text-right">$109.09</td></tr>',
        subtotal: '$109.09',
        tax: '$10.91',
        logoUrl: logoAbsoluteUrl()
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            const response = await fetch(API_ENDPOINTS.ADMIN_TEMPLATES);
            if (response.ok) {
                const data = await response.json();
                setTemplates(data);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            setMessage({ type: 'error', text: 'Failed to load templates' });
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (template) => {
        setSelectedTemplate(template);
        setEditContent(template.content || '');
        setEditSubject(template.subject || '');
        setMessage({ type: '', text: '' });
        setViewMode('code');
    };

    const handleSave = async () => {
        if (!selectedTemplate) return;
        setSaving(true);
        try {
            const response = await fetch(API_ENDPOINTS.ADMIN_TEMPLATES, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...selectedTemplate,
                    subject: editSubject,
                    content: editContent
                })
            });

            if (response.ok) {
                setMessage({ type: 'success', text: 'Template saved successfully!' });
                fetchTemplates();
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error saving template' });
        } finally {
            setSaving(false);
        }
    };

    const getPreviewHtml = () => {
        let preview = editContent;
        // Handle Triple Mustache
        Object.keys(mockData).forEach(key => {
            const regex = new RegExp(`{{{${key}}}}`, 'g');
            preview = preview.replace(regex, mockData[key]);
        });
        // Handle Double Mustache
        Object.keys(mockData).forEach(key => {
            const regex = new RegExp(`{{${key}}}`, 'g');
            preview = preview.replace(regex, mockData[key]);
        });
        // Handle Simple Conditionals (True)
        Object.keys(mockData).forEach(key => {
            const regex = new RegExp(`{{#${key}}}([\\s\\S]*?){{\\/${key}}}`, 'g');
            preview = preview.replace(regex, '$1');
        });
        // Handle Negative Conditionals (False)
        preview = preview.replace(/{{\^[^}]+}}[\s\S]*?{{\/[^}]+}}/g, '');

        return preview;
    };

    if (loading) {
        return <div className="p-8 text-center text-[#ebdbb2]">Loading templates...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-[#ebdbb2]">System Template Manager</h2>
                    <p className="text-[#bdae93]">Manage email and PDF templates across the system</p>
                </div>
            </div>

            {message.text && (
                <div className={`p-4 rounded-lg flex items-center space-x-3 ${message.type === 'success' ? 'bg-[#b8bb26] text-[#1d2021]' : 'bg-[#fb4934] text-[#ebdbb2]'
                    }`}>
                    <FaInfoCircle />
                    <span>{message.text}</span>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* List */}
                <div className="lg:col-span-1 bg-[#282828] rounded-xl border border-[#3c3836] overflow-hidden">
                    <div className="p-4 border-b border-[#3c3836] bg-[#3c3836]/30">
                        <h3 className="font-semibold text-[#ebdbb2]">Templates</h3>
                    </div>
                    <div className="divide-y divide-[#3c3836] max-h-[70vh] overflow-y-auto">
                        {templates.map(t => (
                            <button
                                key={t.templateId}
                                onClick={() => handleSelect(t)}
                                className={`w-full text-left p-4 hover:bg-[#3c3836] transition-colors flex items-center space-x-3 ${selectedTemplate?.templateId === t.templateId ? 'bg-[#3c3836] border-l-4 border-l-[#fabd2f]' : ''
                                    }`}
                            >
                                {t.type === 'email' ? <FaEnvelope className="text-[#8ec07c]" /> : <FaFilePdf className="text-[#fb4934]" />}
                                <div>
                                    <div className="text-sm font-medium text-[#ebdbb2]">{t.name}</div>
                                    <div className="text-xs text-[#bdae93] font-mono">{t.templateId}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Editor */}
                <div className="lg:col-span-3 space-y-4">
                    {selectedTemplate ? (
                        <div className="bg-[#282828] rounded-xl border border-[#3c3836] p-6 space-y-4">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-3 text-[#ebdbb2]">
                                    <button
                                        onClick={() => setViewMode('code')}
                                        className={`px-4 py-2 rounded-lg font-medium transition-all ${viewMode === 'code' ? 'bg-[#fabd2f] text-[#282828]' : 'text-[#bdae93] hover:text-[#ebdbb2]'}`}
                                    >
                                        <FaCode className="inline mr-2" /> Code
                                    </button>
                                    <button
                                        onClick={() => setViewMode('preview')}
                                        className={`px-4 py-2 rounded-lg font-medium transition-all ${viewMode === 'preview' ? 'bg-[#fabd2f] text-[#282828]' : 'text-[#bdae93] hover:text-[#ebdbb2]'}`}
                                    >
                                        <FaEnvelope className="inline mr-2" /> Preview
                                    </button>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => {
                                            if (window.confirm('Are you sure you want to discard changes?')) {
                                                handleSelect(selectedTemplate);
                                            }
                                        }}
                                        className="inline-flex items-center px-4 py-2 bg-[#504945] text-[#ebdbb2] rounded-lg hover:bg-[#665c54] transition-colors"
                                    >
                                        <FaUndo className="mr-2" /> Reset
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="inline-flex items-center px-4 py-2 bg-[#b8bb26] text-[#1d2021] rounded-lg hover:bg-[#8ec07c] transition-colors font-bold disabled:opacity-50 shadow-lg shadow-[#b8bb26]/10"
                                    >
                                        <FaSave className="mr-2" /> {saving ? 'Saving...' : 'Save Template'}
                                    </button>
                                </div>
                            </div>

                            {viewMode === 'code' ? (
                                <>
                                    {selectedTemplate.type === 'email' && (
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-[#bdae93]">Email Subject</label>
                                            <input
                                                type="text"
                                                value={editSubject}
                                                onChange={(e) => setEditSubject(e.target.value)}
                                                className="w-full bg-[#3c3836] border border-[#504945] rounded-lg px-4 py-2 text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:outline-none"
                                            />
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-[#bdae93]">Template Source (HTML/Handlebars)</label>
                                        <div className="relative">
                                            <textarea
                                                rows={25}
                                                value={editContent}
                                                onChange={(e) => setEditContent(e.target.value)}
                                                className="w-full bg-[#1d2021] border border-[#3c3836] rounded-lg p-4 text-[#8ec07c] font-mono text-sm focus:ring-2 focus:ring-[#fabd2f] focus:outline-none resize-none"
                                            />
                                            <div className="absolute top-2 right-2 text-[#3c3836]">
                                                <FaCode size={24} />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="space-y-4">
                                    {selectedTemplate.type === 'email' && (
                                        <div className="bg-[#3c3836] rounded-lg p-4 border border-[#504945]">
                                            <span className="text-[#bdae93] text-sm mr-2">Subject:</span>
                                            <span className="text-[#ebdbb2] font-medium">{editSubject.replace(/{{2,3}([a-zA-Z0-9_.]+)}{2,3}/g, (match, key) => mockData[key] || match)}</span>
                                        </div>
                                    )}
                                    <div className="bg-white rounded-lg min-h-[500px] overflow-hidden border-4 border-[#3c3836]">
                                        <iframe
                                            title="Template Preview"
                                            srcDoc={getPreviewHtml()}
                                            className="w-full h-[600px] border-none"
                                        />
                                    </div>
                                    <div className="bg-[#fabd2f]/10 p-4 rounded-lg border border-[#fabd2f]/20">
                                        <div className="flex items-center space-x-2 text-[#fabd2f] mb-1">
                                            <FaInfoCircle size={14} />
                                            <span className="text-xs font-bold uppercase tracking-wider">Preview Mode</span>
                                        </div>
                                        <p className="text-xs text-[#bdae93]">This preview uses simulated data (e.g., "John Citizen") to show you how the placeholders will look once rendered for a real customer.</p>
                                    </div>
                                </div>
                            )}

                            <div className="bg-[#3c3836]/30 p-4 rounded-lg">
                                <h4 className="text-sm font-semibold text-[#fabd2f] mb-2">Available Variables</h4>
                                <div className="flex flex-wrap gap-2">
                                    {selectedTemplate.variables?.map(v => (
                                        <code key={v} className="bg-[#282828] px-2 py-1 rounded text-[#83a598] text-xs">
                                            {`{{${v}}}`}
                                        </code>
                                    ))}
                                </div>
                                <p className="text-xs text-[#bdae93] mt-2">Use these placeholders to dynamically inject data into your template.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-[#282828] rounded-xl border border-[#3c3836] p-12 text-center text-[#bdae93]">
                            <FaInfoCircle className="mx-auto mb-4" size={48} />
                            <p>Select a template from the list to start editing</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplateManager;
